import Button from '@risksmart-app/components/src/button';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ActionsButton from 'src/components/actions-button/ActionsButton';
import { getSmartCopyTemplateActionState } from 'src/utils/smart-copy';

import { addCustomDatasourceUrl } from '@/utils/urls';

import type { CopyCustomDatasourceNavState } from './customDatasourceSmartCopy';
import { CUSTOM_DATASOURCE_SMART_COPY } from './customDatasourceSmartCopy';

type CustomDatasourceRegisterActionsProps = {
  /**
   * Whether the Custom Data Source Smart Copy flag is on. Read at the call site
   * (`Page.tsx`) via the OpenFeature + Meta.features hybrid, so the same value
   * also gates the register table's row selection.
   */
  isSmartCopyEnabled: boolean;
  /** Rows currently selected in the register table. "Copy" enables at exactly 1. */
  selectedCount: number;
  /**
   * Id of the selected source row, set only when exactly one is selected. Carried
   * to the create page via nav state so it opens pre-populated from that source.
   */
  selectedId?: string;
};

/**
 * Register-header create control for Custom Data Sources.
 *
 * - Flag OFF → the single "Add custom datasource" primary button (the original
 *   control, unchanged).
 * - Flag ON  → a shared `ActionsButton` dropdown with "Add custom datasource"
 *   (always enabled) and "Copy custom datasource" (enabled only when exactly
 *   one row is selected, via `getSmartCopyTemplateActionState`).
 *
 * The caller gates this behind `<Permission permission={'insert:custom_datasource'}>`,
 * so it's hidden entirely for users without create access. Copy opens the create
 * page pre-populated from the selected source (via nav state).
 */
export const CustomDatasourceRegisterActions: FC<
  CustomDatasourceRegisterActionsProps
> = ({ isSmartCopyEnabled, selectedCount, selectedId }) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'customDatasources',
  });
  const navigate = useNavigate();

  if (!isSmartCopyEnabled) {
    return (
      <Button variant={'primary'} href={addCustomDatasourceUrl()}>
        {st('create_new_button')}
      </Button>
    );
  }

  const templateActionState = getSmartCopyTemplateActionState(t, {
    entityNoun: CUSTOM_DATASOURCE_SMART_COPY.entityNoun,
    selectedCount,
  });

  return (
    <ActionsButton
      buttonText={st('actions_button')}
      testId={'customDatasourceActions'}
      variant={'primary'}
      items={[
        {
          id: 'addCustomDatasource',
          text: st('create_new_button'),
          onItemClick: () => navigate(addCustomDatasourceUrl()),
        },
        {
          id: 'copyCustomDatasource',
          text: st('add_custom_datasource_from_template'),
          ...templateActionState,
          onItemClick: () => {
            const state: CopyCustomDatasourceNavState = {
              copyFromCustomDatasourceId: selectedId,
            };
            navigate(addCustomDatasourceUrl(), { state });
          },
        },
      ]}
    />
  );
};
